import { registerSW } from "virtual:pwa-register";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { installSpeechSynthesisGesturePrime } from "./components/ImmersiveKnowledgeNarration";
import { Landing } from "./components/Landing";
import type {
  M1S2CompletionResult,
  M1S2ExperienceEvent,
  M1S2ProgressSnapshot,
  M1S2RemediationSnapshot,
} from "./components/M1S2Experience";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import type {
  RuntimeSceneCompletionResult,
  RuntimeSceneExperienceEvent,
  RuntimeSceneProgressSnapshot,
} from "./components/RuntimeSceneExperience";
import { completedModuleReportIdAfterScene } from "./reporting/learningReportModel";
import { useAppStore } from "./state/appStore";
import { persistRuntimeAssistantAuditEvent } from "./state/runtimeAssistantAudit";
import { LearnerSyncBridge } from "./sync/LearnerSyncBridge";
import { TaskAdjudicationProvider } from "./sync/taskAdjudication";
import { isImmersiveAcceptanceRequest, isVisualAcceptanceRequest } from "./visualAcceptanceRuntime";

const SceneExperience = lazy(() =>
  import("./components/SceneExperience").then((module) => ({ default: module.SceneExperience })),
);
const CourseMap = lazy(() =>
  import("./components/CourseMap").then((module) => ({ default: module.CourseMap })),
);
const M1S2Experience = lazy(() =>
  import("./components/M1S2Experience").then((module) => ({
    default: module.M1S2Experience,
  })),
);
const RuntimeSceneRoute = lazy(() =>
  import("./components/RuntimeSceneRoute").then((module) => ({
    default: module.RuntimeSceneRoute,
  })),
);
const LearningReport = lazy(() =>
  import("./components/LearningReport").then((module) => ({ default: module.LearningReport })),
);
const TeacherPreview = lazy(() =>
  import("./components/TeacherPreview").then((module) => ({ default: module.TeacherPreview })),
);
const VisualAcceptanceGallery = lazy(() =>
  import("./components/VisualAcceptanceGallery").then((module) => ({
    default: module.VisualAcceptanceGallery,
  })),
);
const ImmersiveAcceptanceWorkbench = lazy(() =>
  import("./components/ImmersiveAcceptanceWorkbench").then((module) => ({
    default: module.ImmersiveAcceptanceWorkbench,
  })),
);
function RouteFallback() {
  return (
    <div className="route-fallback" role="status">
      <i />
      <span>正在展开历史档案剧场…</span>
    </div>
  );
}

function PwaPrompt() {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [updateServiceWorker, setUpdateServiceWorker] = useState<
    ((reloadPage?: boolean) => Promise<void>) | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    const register = () => {
      if (cancelled) return;
      const update = registerSW({
        immediate: true,
        onNeedRefresh: () => setNeedsRefresh(true),
        onOfflineReady: () => setOfflineReady(true),
      });
      setUpdateServiceWorker(() => update);
    };

    const idleHandle =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(register, { timeout: 2_500 })
        : null;
    const timeoutHandle = idleHandle === null ? window.setTimeout(register, 1_200) : null;

    return () => {
      cancelled = true;
      if (idleHandle !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
    };
  }, []);

  if (!needsRefresh && !offlineReady) return null;

  return (
    <div className="pwa-toast" role="status">
      <div>
        <strong>{needsRefresh ? "新版本已准备好" : "核心场景已可离线续学"}</strong>
        <span>{needsRefresh ? "刷新后使用最新内容包。" : "断网时仍可继续当前研学。"}</span>
      </div>
      {needsRefresh && (
        <button type="button" onClick={() => updateServiceWorker?.(true)}>
          刷新
        </button>
      )}
      <button
        type="button"
        aria-label="关闭提示"
        onClick={() => {
          setNeedsRefresh(false);
          setOfflineReady(false);
        }}
      >
        ×
      </button>
    </div>
  );
}

function LearnerApplication() {
  const {
    view,
    reportScope,
    profile,
    currentSceneId,
    settings,
    sceneProgressById,
    setView,
    saveSceneProgress,
    registerSceneTaskResult,
    completeScene,
    completeTask,
    openModuleReport,
    record,
  } = useAppStore();
  const resolvedView = profile || view === "landing" || view === "teacher" ? view : "landing";
  const m1s2Progress = sceneProgressById.M1S2;
  const runtimeProgress = sceneProgressById[currentSceneId];
  const reportRouteKey =
    reportScope.kind === "module" ? `module:${reportScope.moduleId}` : "course";
  const routeKey = `${resolvedView}:${currentSceneId}:${reportRouteKey}`;
  const m1s2InitialRemediation: M1S2RemediationSnapshot | undefined =
    m1s2Progress?.remediation?.sourceTaskId === "T04"
      ? {
          ...m1s2Progress.remediation,
          sourceTaskId: "T04",
        }
      : undefined;

  const handleM1S2Event = useCallback(
    (event: M1S2ExperienceEvent) => {
      const payload = {
        sequenceId: event.sequenceId,
        sequenceIndex: event.sequenceIndex,
        ...(event.details ?? {}),
      };
      switch (event.name) {
        case "scene_entered":
          record("scene_entered", { entry: "module_map", ...payload }, { sceneId: event.sceneId });
          break;
        case "content_viewed":
          record("content_viewed", payload, { sceneId: event.sceneId });
          break;
        case "source_viewed":
          record("source_viewed", payload, { sceneId: event.sceneId });
          break;
        case "comparison_explored":
        case "scene_hotspot_explored":
          record("interaction_completed", payload, { sceneId: event.sceneId });
          break;
        case "task_evaluated": {
          const correct = event.details?.correct === true;
          const attempt = registerSceneTaskResult(event.sceneId, event.sequenceId, correct);
          record(
            "task_answered",
            { ...payload, attempt },
            { sceneId: event.sceneId, taskId: event.sequenceId },
          );
          break;
        }
        case "remediation_variant_evaluated":
          record(
            "interaction_completed",
            { interaction: "remediation_variant_answer", ...payload },
            {
              sceneId: event.sceneId,
              taskId: "T04",
            },
          );
          break;
        case "remediation_started":
          record("remediation_started", payload, {
            sceneId: event.sceneId,
            taskId: event.sequenceId,
          });
          break;
        case "remediation_completed":
          record("remediation_completed", payload, {
            sceneId: event.sceneId,
            taskId: event.sequenceId,
          });
          break;
        case "evidence_revealed":
          record(
            "evidence_awarded",
            { evidenceId: "M1_EVIDENCE_PROTRACTED_WAR", ...payload },
            { sceneId: event.sceneId },
          );
          break;
        case "scene_completed":
          record("scene_completed", payload, { sceneId: event.sceneId });
          break;
        case "scene_exited":
          record("scene_exited", payload, { sceneId: event.sceneId });
          break;
      }
    },
    [record, registerSceneTaskResult],
  );

  const handleM1S2Progress = useCallback(
    (progress: M1S2ProgressSnapshot) => {
      saveSceneProgress(progress.sceneId, {
        activeSequenceId: progress.activeSequenceId,
        stage: progress.stage,
        completedTaskIds: [...progress.completedTaskIds],
        ...(progress.remediation ? { remediation: { ...progress.remediation } } : {}),
      });
    },
    [saveSceneProgress],
  );

  const handleM1S2Complete = useCallback(
    (result: M1S2CompletionResult) => {
      for (const taskId of result.completedTaskIds) completeTask(taskId);
      completeScene(result.sceneId);
      record(
        "scene_exited",
        { completed: true, claimCount: result.claimIds.length },
        { sceneId: result.sceneId },
      );
      const completedSceneIds = useAppStore.getState().completedSceneIds;
      const completedModuleId = completedModuleReportIdAfterScene(
        result.sceneId,
        completedSceneIds,
      );
      if (completedModuleId) {
        openModuleReport(completedModuleId);
        return;
      }
      setView("map");
    },
    [completeScene, completeTask, openModuleReport, record, setView],
  );

  const handleRuntimeEvent = useCallback(
    (event: RuntimeSceneExperienceEvent) => {
      if (persistRuntimeAssistantAuditEvent(event, record)) return;

      const payload = {
        sequenceId: event.sequenceId,
        sequenceIndex: event.sequenceIndex,
        ...(event.details ?? {}),
      };

      switch (event.name) {
        case "scene_entered":
          record("scene_entered", { entry: "module_map", ...payload }, { sceneId: event.sceneId });
          break;
        case "content_viewed":
          record("content_viewed", payload, { sceneId: event.sceneId });
          break;
        case "source_viewed":
          record("source_viewed", payload, { sceneId: event.sceneId });
          break;
        case "interaction_completed":
        case "extension_entered":
        case "review_started":
        case "scene_ready":
          record(
            "interaction_completed",
            { runtimeEvent: event.name, ...payload },
            { sceneId: event.sceneId },
          );
          break;
        case "task_evaluated": {
          const correct = event.details?.correct === true;
          const attempt = registerSceneTaskResult(event.sceneId, event.sequenceId, correct);
          record(
            "task_answered",
            { ...payload, attempt },
            { sceneId: event.sceneId, taskId: event.sequenceId },
          );
          break;
        }
        case "scene_completed":
          record("scene_completed", payload, { sceneId: event.sceneId });
          break;
        case "scene_exited":
          record("scene_exited", payload, { sceneId: event.sceneId });
          break;
      }
    },
    [record, registerSceneTaskResult],
  );

  const handleRuntimeProgress = useCallback(
    (progress: RuntimeSceneProgressSnapshot) => {
      saveSceneProgress(progress.sceneId, {
        activeSequenceId: progress.activeSequenceId,
        stage: progress.stage,
        completedNodeIds: [...progress.completedNodeIds],
        completedTaskIds: [...progress.completedTaskIds],
      });
    },
    [saveSceneProgress],
  );

  const handleRuntimeComplete = useCallback(
    (result: RuntimeSceneCompletionResult) => {
      for (const taskId of result.completedTaskIds) completeTask(taskId);
      completeScene(result.sceneId);
      record(
        "scene_exited",
        { completed: true, claimCount: result.claimIds.length },
        { sceneId: result.sceneId },
      );
      const completedSceneIds = useAppStore.getState().completedSceneIds;
      const completedModuleId = completedModuleReportIdAfterScene(
        result.sceneId,
        completedSceneIds,
      );
      if (completedModuleId) {
        openModuleReport(completedModuleId);
        return;
      }
      setView("map");
    },
    [completeScene, completeTask, openModuleReport, record, setView],
  );

  useEffect(() => {
    document.documentElement.classList.toggle("reduced-motion", settings.reducedMotion);
    document.documentElement.classList.toggle("high-contrast", settings.highContrast);
  }, [settings.highContrast, settings.reducedMotion]);

  useEffect(() => {
    if (resolvedView !== view) setView(resolvedView);
    document.documentElement.dataset.currentView = resolvedView;
    window.scrollTo(0, 0);
  }, [resolvedView, setView, view]);

  useEffect(() => {
    if (!routeKey) return;
    const focusRouteMain = () => {
      const main = document.querySelector<HTMLElement>("main");
      if (!main) return false;
      main.tabIndex = -1;
      main.focus({ preventScroll: true });
      return true;
    };

    if (focusRouteMain()) return;

    const observer = new MutationObserver(() => {
      if (focusRouteMain()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [routeKey]);

  return (
    <TaskAdjudicationProvider>
      <LearnerSyncBridge />
      {resolvedView === "landing" && <Landing />}
      <RouteErrorBoundary key={`${resolvedView}:${currentSceneId}`}>
        <Suspense fallback={<RouteFallback />}>
          {resolvedView === "map" && <CourseMap />}
          {resolvedView === "scene" && currentSceneId === "M1S1" && <SceneExperience />}
          {resolvedView === "scene" && currentSceneId === "M1S2" && (
            <M1S2Experience
              reducedMotion={settings.reducedMotion}
              subtitles={settings.subtitles}
              narrationVolume={settings.narrationVolume}
              initialStage={
                m1s2Progress?.stage === "evidence"
                  ? "evidence"
                  : m1s2Progress?.stage === "remediation"
                    ? "remediation"
                    : "sequence"
              }
              initialCompletedTaskIds={m1s2Progress?.completedTaskIds ?? []}
              initialAttemptCounts={m1s2Progress?.taskAttemptCounts ?? {}}
              {...(m1s2InitialRemediation ? { initialRemediation: m1s2InitialRemediation } : {})}
              onBack={() => setView("map")}
              onComplete={handleM1S2Complete}
              onEvent={handleM1S2Event}
              onProgress={handleM1S2Progress}
              {...(m1s2Progress?.activeSequenceId
                ? { initialSequenceId: m1s2Progress.activeSequenceId }
                : {})}
            />
          )}
          {resolvedView === "scene" && currentSceneId !== "M1S1" && currentSceneId !== "M1S2" && (
            <RuntimeSceneRoute
              sceneId={currentSceneId}
              settings={{
                reducedMotion: settings.reducedMotion,
                highContrast: settings.highContrast,
                subtitles: settings.subtitles,
                narrationVolume: settings.narrationVolume,
              }}
              initialCompletedNodeIds={runtimeProgress?.completedNodeIds ?? []}
              initialCompletedTaskIds={runtimeProgress?.completedTaskIds ?? []}
              initialAttemptCounts={runtimeProgress?.taskAttemptCounts ?? {}}
              onBack={() => setView("map")}
              onComplete={handleRuntimeComplete}
              onEvent={handleRuntimeEvent}
              onProgress={handleRuntimeProgress}
              {...(runtimeProgress?.activeSequenceId
                ? { initialActiveSequenceId: runtimeProgress.activeSequenceId }
                : {})}
            />
          )}
          {resolvedView === "report" && <LearningReport key={reportRouteKey} />}
          {resolvedView === "teacher" && <TeacherPreview />}
        </Suspense>
      </RouteErrorBoundary>
      <PwaPrompt />
    </TaskAdjudicationProvider>
  );
}

export default function App() {
  useEffect(() => installSpeechSynthesisGesturePrime(), []);

  if (isImmersiveAcceptanceRequest()) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <ImmersiveAcceptanceWorkbench />
      </Suspense>
    );
  }
  if (isVisualAcceptanceRequest()) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <VisualAcceptanceGallery />
      </Suspense>
    );
  }
  return <LearnerApplication />;
}
